const mobileQuery = window.matchMedia('(max-width: 749px)');
const dragThreshold = 8;

/**
 * Shopify custom element section.
 *
 * Liquid renders <figma-scene-solutions> and loads this file with `defer`, so the browser
 * upgrades the element and this class owns init / teardown. The `customElements.get` guard
 * is required: the same section can be placed several times on a page, which emits the same
 * asset URL more than once, and a tag name cannot be redefined once registered.
 *
 * Anything bound outside the element (here: the window resize listener and its animation
 * frame) is stored on the instance and released in disconnectedCallback, so re-rendering the
 * section in the theme editor cannot leak listeners.
 */
class FigmaSceneSolutions extends HTMLElement {
  connectedCallback() {
    // Moving an already upgraded node within the document re-runs connectedCallback.
    if (this.dataset.sceneSolutionsReady) {
      return;
    }

    this.dataset.sceneSolutionsReady = 'true';

    const tabs = Array.from(this.querySelectorAll('[data-scene-tab]'));
    const sceneLayers = Array.from(this.querySelectorAll('[data-scene-media], [data-scene-panel]'));
    const scrollAreas = Array.from(this.querySelectorAll('[data-scene-scroll]'));
    const cartDrawer = document.querySelector('cart-drawer');
    let hasCenteredMobileScenes = false;

    // Arrow functions: these bodies read the element through `this`, which a plain
    // function declaration would not bind to the instance.
    const setActiveScene = (sceneId) => {
      this.dataset.activeScene = sceneId;

      tabs.forEach((tab) => {
        const isActive = tab.dataset.sceneId === sceneId;

        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      sceneLayers.forEach((layer) => {
        layer.dataset.active = String(layer.dataset.sceneId === sceneId);
      });
    };

    const activateTab = (tab, { focus = false } = {}) => {
      if (!tab) {
        return;
      }

      setActiveScene(tab.dataset.sceneId || '1');

      if (focus) {
        tab.focus();
      }
    };

    tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');

      tab.addEventListener('click', () => {
        activateTab(tab);
      });

      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabs.length - 1;
        }

        if (nextIndex === null) {
          return;
        }

        event.preventDefault();
        activateTab(tabs[nextIndex], { focus: true });
      });
    });

    const initialTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];

    if (initialTab) {
      setActiveScene(initialTab.dataset.sceneId || '1');
    }

    // Resolved once: the hints never change identity, and the resize handler runs on
    // every animation frame while the window is being dragged.
    const dragHints = scrollAreas.map(
      (area) => area.closest('[data-scene-media]')?.querySelector('.figma-scene-solutions__drag-hint') || null
    );

    /**
     * Reads scrollWidth/clientWidth once per element and does both jobs in a single
     * pass: reading them interleaved with writes would force a sync layout each time.
     */
    function measureAndApplyMobileScenes() {
      if (!mobileQuery.matches) {
        return;
      }

      const shouldCenter = !hasCenteredMobileScenes;

      scrollAreas.forEach((area, index) => {
        const overflow = area.scrollWidth - area.clientWidth;
        const hint = dragHints[index];

        if (overflow > dragThreshold) {
          if (shouldCenter) {
            area.scrollLeft = overflow / 2;
          }

          hint?.classList.remove('figma-scene-solutions__drag-hint--hidden');
          // A swipeable region has to be reachable without a pointer too (WCAG 2.1.1),
          // otherwise keyboard users cannot pan the scene at all.
          area.setAttribute('tabindex', '0');
        } else {
          area.scrollLeft = 0;
          area.removeAttribute('tabindex');
          hint?.classList.add('figma-scene-solutions__drag-hint--hidden');
        }
      });

      hasCenteredMobileScenes = true;
    }

    function resetMobileSceneState() {
      hasCenteredMobileScenes = false;

      scrollAreas.forEach((area, index) => {
        area.removeAttribute('tabindex');
        dragHints[index]?.classList.remove('figma-scene-solutions__drag-hint--hidden');
      });
    }

    // Tracked so a resize only re-centres when the breakpoint actually flips.
    let wasMobile = mobileQuery.matches;

    // resize fires continuously while dragging, so collapse it into one run per frame.
    this.handleWindowResize = () => {
      if (this.resizeFrame) {
        return;
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = 0;

        if (!mobileQuery.matches) {
          if (wasMobile) {
            wasMobile = false;
            resetMobileSceneState();
          }

          return;
        }

        if (!wasMobile) {
          wasMobile = true;
          hasCenteredMobileScenes = false;
        }

        measureAndApplyMobileScenes();
      });
    };

    window.addEventListener('resize', this.handleWindowResize);
    window.requestAnimationFrame(() => measureAndApplyMobileScenes());

    function setButtonLoading(trigger, isLoading) {
      if (!trigger) {
        return;
      }

      const label = trigger.querySelector('.figma-scene-solutions__cta-label');
      const defaultLabel = trigger.dataset.defaultLabel || label?.textContent || '';
      const loadingLabel = trigger.dataset.loadingLabel || defaultLabel;

      trigger.disabled = isLoading;
      trigger.classList.toggle('is-loading', isLoading);
      trigger.setAttribute('aria-busy', String(isLoading));

      if (label) {
        label.textContent = isLoading ? loadingLabel : defaultLabel;
      }

      if (!isLoading) {
        trigger.removeAttribute('aria-busy');
      }
    }

    function getSectionsToBundle() {
      const sectionsToBundle = [];

      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      return sectionsToBundle;
    }

    function requestCartAdd(config) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open(config.method || 'POST', theme.routes.cart_add_url, true);

        Object.entries(config.headers || {}).forEach(([name, value]) => {
          if (value !== undefined) {
            xhr.setRequestHeader(name, value);
          }
        });

        xhr.onload = () => {
          let parsedState;

          try {
            parsedState = JSON.parse(xhr.responseText);
          } catch (error) {
            reject(error);
            return;
          }

          resolve(parsedState);
        };

        xhr.onerror = () => reject(new Error('Cart add request failed'));
        xhr.send(config.body);
      });
    }

    function publishCartError(parsedState, variantIds) {
      theme.pubsub?.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
        source: 'figma-scene-solutions',
        productVariantId: variantIds,
        errors: parsedState.errors || parsedState.description,
        message: parsedState.message
      });
    }

    function handleCartAddState(parsedState, variantIds, trigger) {
      if (parsedState.status) {
        publishCartError(parsedState, variantIds);
        return;
      }

      if (!cartDrawer) {
        theme.pubsub?.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'figma-scene-solutions',
          productVariantId: variantIds,
          cart: { item_count: parsedState.item_count || 0, sections: parsedState.sections || {} }
        });
        return;
      }

      const cartDrawerSection = parsedState.sections?.[cartDrawer.dataset.sectionId];
      const sectionDocument = cartDrawerSection
        ? new DOMParser().parseFromString(cartDrawerSection, 'text/html')
        : null;
      const itemCount = Number.parseInt(sectionDocument?.querySelector('cart-count')?.textContent || '0', 10);

      theme.pubsub?.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'figma-scene-solutions',
        productVariantId: variantIds,
        cart: {
          item_count: Number.isNaN(itemCount) ? 0 : itemCount,
          sections: parsedState.sections || {}
        }
      });

      cartDrawer.show(trigger);
    }

    function postCartAdd(items) {
      const config = theme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items,
        sections: getSectionsToBundle(),
        sections_url: window.location.pathname
      });

      return requestCartAdd(config);
    }

    /**
     * A single sold out variant rejects the whole /cart/add.json batch with a 422.
     * Retry item by item so the purchasable part of the scene still lands in the cart.
     */
    function postCartAddIndividually(items) {
      const added = [];
      const failed = [];
      let lastState = null;

      return items
        .reduce((chain, item) => chain
          .then(() => postCartAdd([item])
            .then((parsedState) => {
              if (parsedState.status) {
                failed.push(item);
                return;
              }

              added.push(item);
              lastState = parsedState;
            })
            .catch(() => {
              failed.push(item);
            })), Promise.resolve())
        .then(() => ({ added, failed, lastState }));
    }

    const addSceneProducts = (trigger) => {
      if (!window.theme?.routes || !window.theme?.utils || !trigger || trigger.disabled) {
        return;
      }

      const sceneId = trigger.dataset.sceneId || this.dataset.activeScene || '1';
      const panel = this.querySelector(`[data-scene-panel][data-scene-id="${sceneId}"]`);
      const items = Array.from(panel?.querySelectorAll('[data-scene-variant-id]') || [])
        .map((node) => ({
          id: Number.parseInt(node.dataset.sceneVariantId, 10),
          quantity: 1
        }))
        .filter((item) => Number.isInteger(item.id) && item.id > 0);

      if (!items.length) {
        return;
      }

      setButtonLoading(trigger, true);

      postCartAdd(items)
        .then((parsedState) => {
          if (!parsedState.status) {
            handleCartAddState(parsedState, items.map((item) => item.id).join(','), trigger);
            return null;
          }

          const batchError = parsedState;

          return postCartAddIndividually(items).then(({ added, failed, lastState }) => {
            if (!added.length) {
              publishCartError(batchError, items.map((item) => item.id).join(','));
              return null;
            }

            if (failed.length) {
              console.warn(
                '[figma-scene-solutions] skipped unavailable variant(s):',
                failed.map((item) => item.id).join(', ')
              );
            }

            handleCartAddState(lastState, added.map((item) => item.id).join(','), trigger);
            return null;
          });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setButtonLoading(trigger, false);
        });
    };

    this.addEventListener('click', (event) => {
      const addAllButton = event.target.closest('[data-scene-add-all]');

      if (!addAllButton) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      addSceneProducts(addAllButton);
    }, true);
  }

  disconnectedCallback() {
    if (this.handleWindowResize) {
      window.removeEventListener('resize', this.handleWindowResize);
      this.handleWindowResize = null;
    }

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = 0;
    }
  }
}

if (!customElements.get('figma-scene-solutions')) {
  customElements.define('figma-scene-solutions', FigmaSceneSolutions);
}

// Editor-only convenience, registered once for the whole document: when a block is
// selected in the theme editor, move focus to the tab that controls it.
document.addEventListener('shopify:block:select', (event) => {
  const tab = event.target.closest?.('[data-scene-tab]');

  if (tab) {
    tab.focus();
  }
});
